import { useEffect, useState } from 'react';
import { Sun, Moon } from 'lucide-react';

type Theme = 'light' | 'dark';

function getSystemTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getStoredTheme(): Theme | null {
  try {
    const stored = localStorage.getItem('app-theme');
    if (stored === 'light' || stored === 'dark') return stored;
    // Migrate legacy values
    if (stored === 'theme-high-contrast' || stored === 'theme-dark') return 'dark';
    if (stored === 'theme-vivid' || stored === 'theme-warm' || stored === 'theme-accent') return 'light';
  } catch { /* ignore */ }
  return null;
}

export default function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(() => getStoredTheme() ?? getSystemTheme());

  useEffect(() => {
    const root = document.documentElement;
    // Clean up any legacy classes
    root.classList.remove('theme-vivid', 'theme-high-contrast');

    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    try {
      localStorage.setItem('app-theme', theme);
    } catch { /* ignore */ }
  }, [theme]);

  const toggle = () => setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));

  const Icon = theme === 'light' ? Sun : Moon;

  return (
    <button
      type="button"
      title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
      aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
      onClick={toggle}
      className="inline-flex items-center justify-center rounded-md border px-2 py-1.5 text-sm hover:bg-muted/50 transition-colors cursor-pointer"
    >
      <Icon className="h-4 w-4" />
      <span className="sr-only">{theme === 'light' ? 'Dark mode' : 'Light mode'}</span>
    </button>
  );
}
